import type { VizChartKindKey } from '../../../Generated/VizChartKind.ts'
import type { VizDirective } from '../../../Generated/VizDirective.ts'
import type { SinkTypeKey } from 'View/Component/ConfigurableVisualization/Sinks/SinkRegistry.js'
import type { NO_TRAIT_MODE, SinkConfig, SourceConfig, SourceSharedState } from 'Flux/Slice/Visualization/VisualizationSliceTypes.js'
import type { VisualizationPipeline } from 'View/Component/SampleGrouping/RunPipeline.js'
import { groupByValue } from 'View/Component/SampleGrouping/Operations/GroupOperation.js'
import { BlastSource } from 'Model/Project/BlastSource.js'

// Identity/labeling helpers (directiveKey, cubeSourceId, isCubeChartKind, …) live in the Flux layer so the
// slice + epic can share them: Flux/Slice/Chat/chatViz.ts. This module holds only the sink-registry-coupled
// mapping and the directive→workspace-source translation.

/**
 * Maps a chat `VizChartKind` to a workspace sink kind. OTU_TABLE and KRONA are intentionally ABSENT —
 * they render standalone (a lightweight table / a Krona iframe), NOT through the cube workspace.
 */
export const CUBE_SINK_BY_KIND: Partial<Record<VizChartKindKey, SinkTypeKey>> = {
  PIE_CHART: 'pieChart',
  TREEMAP: 'treemap',
  STACKED_BAR: 'stackedBar',
  RANKED_BAR: 'rankedBar',
  AREA_CHART: 'areaChart',
  LINE_CHART: 'lineChart',
  MAP: 'map',
  BETA_DIVERSITY: 'betaDiversity',
  RICHNESS: 'biodiversityRichness',
  SHANNON: 'biodiversityShannon',
  GINI_SIMPSON: 'biodiversityGiniSimpson',
  ABSOLUTE_EFFECTIVE: 'biodiversityAbsoluteEffective',
  OVERLAP_HEATMAP: 'overlapHeatmap',
  UPSET: 'upset',
  SHARED_UNIQUE: 'sharedUnique'
}

/**
 * The pipeline the chat uses for a cube source. Bucketing:
 * - `aggregate` (single-bucket sinks: pie/treemap): NO groupBy → one "all" bucket over every sample, i.e.
 *   the whole-scope composition. (A per-sample groupBy would leave the pie showing only one sample.)
 *   groupByField is ignored here — a single-bucket sink shows one bucket regardless.
 * - multi-bucket sinks (stacked bar, biodiversity, …): one bucket per `groupByField` value when the agent
 *   asked to group by a metadata field (e.g. per habitat / country / order — the G2 comparison substrate),
 *   otherwise the default one-bucket-per-sample.
 * `sampleIds` (when set) restricts the source to that explicit sample slice; applied in BlockingSourceEpic
 * BEFORE bucketing. This mirrors how the real workspace applies grouping + sample selection to the cube.
 */
export function chatSourceConfig(d: VizDirective, aggregate: boolean): SourceConfig {
  const groupField = d.groupByField
  const groupBys = aggregate
    ? []
    : [{ operationKey: groupByValue.key, metaDataField: groupField ?? 'sampleMetadata.customerSampleName', config: {} }]
  const sampleIds = d.sampleIds && d.sampleIds.length > 0 ? d.sampleIds : undefined
  return {
    kind: 'pipeline/v1',
    sampleIds,
    pipeline: { filters: [], groupBys, projections: [], sortBys: [] } as unknown as VisualizationPipeline
  }
}

/**
 * Merge the directive's per-sink tuning bag onto a registry-built sink config's params. The
 * backend has already validated the keys against the capability manifest, so we forward them verbatim —
 * exactly how the workspace's own per-sink controls mutate `SinkConfig.params` (see e.g. MapSink's
 * displayModel `registerSink`). Directive params override the registry defaults.
 */
export function chatSinkConfig(base: SinkConfig, d: VizDirective): SinkConfig {
  const extra = d.sinkParams
  if (!extra || Object.keys(extra).length === 0) return base
  return { ...base, params: { ...base.params, ...extra } }
}

/** A sensible default rank when the model omits taxonLevel — avoids rendering everything at the top rank. */
const DEFAULT_TAXON_LEVEL: SourceSharedState['taxonLevel'] = 'family'

/** Remap the flat, model-facing directive onto the workspace source's shared render state. */
export function chatSourceShared(d: VizDirective): SourceSharedState {
  const blastType = (d.blastSource && BlastSource[d.blastSource as keyof typeof BlastSource]) || BlastSource.Consensus
  return {
    blastType,
    taxonLevel: (d.taxonLevel as SourceSharedState['taxonLevel']) ?? DEFAULT_TAXON_LEVEL,
    scalingMode: d.scalingMode as SourceSharedState['scalingMode'],
    countMode: d.countMode as SourceSharedState['countMode'],
    gradeThreshold: d.gradeThreshold,
    selectedPath: d.selectedPath,
    // traitName (single exact trait) filters the whole source to OTUs carrying it, then renders the normal
    // taxonomic breakdown — it wins over traitGroup. traitGroup alone segments by trait (composition);
    // noTraitMode only matters for that group overlay.
    natureKPIs:
      d.traitName || d.traitGroup
        ? {
            traitGroup: d.traitGroup ?? '',
            traitName: d.traitName || undefined,
            noTraitMode: (d.noTraitMode === 'exclude' ? 'exclude' : 'include') as NO_TRAIT_MODE
          }
        : undefined,
    // SPIN mode: a spin algorithm switches this into the (sample × spin) cube (own cube key → own fetch,
    // does not evict the normal cube). Empty atTime = latest. Only set when the directive asks for spin.
    spin: d.spinAlgorithm ? { algorithmType: d.spinAlgorithm, atTime: d.spinAtTime ?? '' } : undefined
  }
}
